import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useAuthStore } from '../store/authStore';
import { api } from '../lib/apiClient';
import type { User } from '../types/user';
import { Seo } from '../components/Seo';

export function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const [name, setName] = useState(user?.displayName ?? '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!user) return <Navigate to="/" replace />;

  const isDirty = name.trim() !== user.displayName;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || !isDirty) return;

    setSaving(true);
    setSuccess(false);
    setError('');

    try {
      const updated = await api.put<User>('/users/me', { displayName: trimmed });
      setUser(updated);
      setSuccess(true);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? '';
      // 409 from backend has Russian text already
      setError(msg || 'Не удалось сохранить. Попробуйте позже.');
    } finally {
      setSaving(false);
    }
  }

  const history = user.displayNameHistory ?? [];

  return (
    <PageWrapper>
      <Seo title="Профиль" path="/profile" noindex />
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">
            ◆ Профиль ◆
          </p>
          <h1 className="font-display text-4xl font-bold text-mafia-paper mb-2">
            Личный кабинет
          </h1>
          <p className="text-mafia-paper-dim text-sm">Управление игровым профилем</p>
        </div>

        <OrnamentDivider className="mb-10" />

        {/* Profile card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="card-vintage p-8 space-y-8"
        >
          {/* Email (read-only) */}
          <div>
            <label className="block font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2">
              Электронная почта
            </label>
            <p className="font-mono text-sm text-mafia-paper-dim bg-mafia-black/40 border border-mafia-border px-4 py-3">
              {user.email}
            </p>
            <p className="text-mafia-muted text-xs font-mono mt-1">
              Почта привязана к аккаунту Google и не может быть изменена
            </p>
          </div>

          {/* Display name form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2">
                Игровое имя
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(''); setSuccess(false); }}
                maxLength={40}
                className="w-full bg-mafia-card border border-mafia-border text-mafia-paper font-mono text-sm px-4 py-3 focus:outline-none focus:border-mafia-gold transition-colors"
              />
              <p className="text-mafia-muted text-xs font-mono mt-1">
                Под этим именем вас будут знать за игровым столом
              </p>
            </div>

            {error && (
              <p className="text-amber-400 text-xs font-mono leading-relaxed border border-amber-400/20 bg-amber-400/5 px-4 py-3">
                {error}
              </p>
            )}

            {success && (
              <p className="text-emerald-400 text-xs font-mono border border-emerald-400/20 bg-emerald-400/5 px-4 py-3">
                Игровое имя сохранено
              </p>
            )}

            <button
              type="submit"
              disabled={saving || !isDirty}
              className="btn-gold-solid w-full py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </form>

          {/* Name history */}
          {history.length > 0 && (
            <>
              <OrnamentDivider variant="dim" />
              <div>
                <p className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-4">
                  История имён
                </p>
                <ul className="space-y-2">
                  {[...history].reverse().map((entry, idx) => (
                    <li key={idx} className="flex items-center justify-between font-mono text-xs">
                      <span className="text-mafia-paper-dim">{entry.name}</span>
                      <span className="text-mafia-muted">
                        {new Date(entry.changedAt).toLocaleDateString('ru-RU', {
                          day: 'numeric', month: 'long', year: 'numeric',
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-mafia-muted text-xs font-mono mt-3">
                  В архивных рейтингах прошлых сезонов рядом с вашим новым именем будет отображаться прежнее
                </p>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </PageWrapper>
  );
}
